<template>
  <div>
    <input type="file" ref="imagesFileRef" @change="onImagesFileChangeWrapper" class="d-none" multiple="multiple"
           :accept="imageFileFormats.join(',')">
    <b-dropdown-item @click="$refs.imagesFileRef.click()" :disabled="isUploading || disabled">
      <font-awesome-icon icon="upload" class="icon mr-2" v-if="!hideIcon"/>
      {{ $t('Upload Images') }}
    </b-dropdown-item>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import {Utils} from "@/core/services/utils.service";
  import InventoryService from "@/modules/inventory/InventoryService";
  import _ from 'lodash';
  import {convertToHeic} from "../../core/helpers";

  const {mapState: mapStateGlobal, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');

  export default {
    name: "UploadImagesDropdownItem",
    props: {
      disabled: {
        type: Boolean,
        default: false
      },
      auctionUuid: {
        type: String,
        required: false,
        default: () => null
      },
      hideIcon: {
        type: Boolean,
        default: false
      },
    },
    data: () => {
      return {
        isUploading: false,
        progress: null
      }
    },
    computed: {
      ...mapStateGlobal({
        imageFileFormats: state => state.imageFileFormats,
        imageMaxFileSize: state => state.imageMaxFileSize,
      })
    },
    methods: {
      ...mapActionsGlobal(['addProgress', 'removeProgress', 'updateProgressValue']),

      async onImagesFileChangeWrapper($event) {
        this.isUploading = true;
        this.progress = await this.addProgress(this.$i18n.t("Bulk inventory images upload"));
        await this.onImagesFileChangeLogic($event);
        this.removeProgress(this.progress.uuid);
        this.$refs.imagesFileRef.value = null;
        this.isUploading = false;
      },
      async onImagesFileChangeLogic($event) {
        const failedFilesData = [];
        const successfulFiles = [];
        const files = $event.target.files;
        const chunkSize = 5;
        let sumSize = 0;

        if (files.length > this.bulkImagesMaxFileCount) {
          this.$errorDialog(
            this.$i18n.t("You can not upload more than {count} images.", {count: this.bulkImagesMaxFileCount}),
            this.$i18n.t('Bulk Upload Validation')
          );
          return;
        }

        for (let file of files) {
          sumSize += file.size;
          const sizeInMbs = file.size / 1000000; // Convert bytes to megabytes
          if (!this.imageFileFormats.includes(file.type)) {
            failedFilesData.push({failure_reason: 'type', file});
          } else if (sizeInMbs > this.imageMaxFileSize) {
            failedFilesData.push({failure_reason: 'size', file});
          } else {
            successfulFiles.push(file);
          }
        }

        const sizeInMbs = sumSize / 1000000; // Convert bytes to megabytes
        if (sizeInMbs > this.imagesZipMaxFileSize) {
          this.$errorDialog(
            this.$i18n.t("Bulk upload size limit exceeded. Max size allowed {size} mbs.", {size: this.imagesZipMaxFileSize}),
            this.$i18n.t('Bulk Upload Validation')
          );
          return;
        }

        if (failedFilesData.length > 0) {
          Utils.parseFailedImagesData(failedFilesData, this);
          return
        }

        const nameToFileMapper = {}
        const images = []
        for (let file of successfulFiles) {
          images.push({name: file.name, size: file.size, type: file.type})
          nameToFileMapper[file.name] = file;
        }
        let payload = {images: images, object_type: 'inventory'}
        if (this.auctionUuid) {
          payload['auction_uuid'] = this.auctionUuid
        }

        let response = await InventoryService.bulkUploadImages(payload)
        if (!response.success) {
          this.handleUploadResponse(response)
          return
        }

        const failedImageNames = []
        const body = response.body
        const bodyImages = body.images
        let failedToDeleteImages = false;
        console.log(body.deleted_images);


        let imgArr = []
        for (let imageName in bodyImages) {
          imgArr.push({image_name: imageName, presigned_url: bodyImages[imageName]})
        }

        let chunkedImgArr = _.chunk(imgArr, chunkSize)
        let successfulCount = 0;

        for (let chunk of chunkedImgArr) {
          let chunkPromises = []
          for (let item of chunk) {
            const presignedUrl = item.presigned_url;
            let file = nameToFileMapper[item.image_name];
            file = await convertToHeic(file);
            chunkPromises.push(new Promise(async (next, rej) => {
              try {
                const base64 = await InventoryService.convertEventTargetToBase64(file)
                const blob = await InventoryService.convertBase64ToBlob(base64)
                await InventoryService.uploadBlobToBucket(presignedUrl, blob)
                successfulCount++;
                this.updateProgressValue({
                  progressValue: parseInt(Math.round(successfulCount / files.length * 100)),
                  uuid: this.progress.uuid
                });
                next()
              } catch (err) {
                failedImageNames.push(item.image_name)
                rej(err)
              }
            }))
          }
          await Promise.all(chunkPromises)
        }

        for (let item of body.deleted_images) {
          const deletionJsonContent = JSON.stringify({
            renamed_images: {}, deleted_images: item.images, object_type: "inventory"
          });
          try {
            await InventoryService.uploadBlobToBucket(item.url, deletionJsonContent, 'application/json')
          } catch (err) {
            failedToDeleteImages = true;
          }
        }

        const errors = []
        if (failedImageNames.length > 0) {
          errors.push(`Failed to upload following image(s): ${failedImageNames.join(', ')}`)
        }
        if (failedToDeleteImages) {
          errors.push(`Failed to process image. Please, refresh the page and try again`)
        }

        if (errors.length > 0) {
          this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, errors));
        }

        this.handleUploadResponse(response);
        this.$emit('imagesUploadFinished', true);
        return true;
      },
      handleUploadResponse(response) {
        const nonFieldErrors = response.body.non_field_errors;
        if (response.success) {
          if (!nonFieldErrors) {
            this.$toast('success', this.$i18n.t('Images were uploaded successfully'));
          } else {
            this.$toast('success', this.$i18n.t('Images were uploaded partially. Please check error message.'));
          }
        }

        if (nonFieldErrors) {
          if (Array.isArray(nonFieldErrors)) {
            Utils.showErrorsInDialog(nonFieldErrors, this);
          } else {
            const dataArr = [];
            for (let item_no in nonFieldErrors) {
              const listItemsArr = [];
              if (nonFieldErrors[item_no] instanceof Array) {
                for (const imageName of nonFieldErrors[item_no]) {
                  const msg = `${imageName} - ${nonFieldErrors[item_no][imageName]}`;
                  listItemsArr.push(this.$createElement('li', {}, msg));
                }
              } else if (nonFieldErrors[item_no] instanceof Object) {
                for (const imageName in nonFieldErrors[item_no]) {
                  const source = nonFieldErrors[item_no][imageName]
                  // TODO should be rewritten in better way
                  let msg
                  if (source instanceof Array) {
                    msg = `${imageName} - ${source.join(', ')}`;
                  } else if (typeof source === "string") {
                    msg = source
                  } else {
                    // TODO handle error
                    continue
                  }
                  listItemsArr.push(this.$createElement('li', {}, msg));
                }
              } else if (typeof nonFieldErrors[item_no] === "string") {
                listItemsArr.push(this.$createElement('li', {}, nonFieldErrors[item_no]));
              }

              dataArr.push(this.$createElement('h5', {class: "mb-0"}, `Item ${item_no}`));
              dataArr.push(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
            }
            this.$errorDialog(this.$createElement('div', {}, dataArr), this.$i18n.t('Image Validation Error'));
          }
        }
      },
    }
  }
</script>

<style scoped>

</style>
